import { CustomModal, StatusBanner } from '@components';
import { FC } from 'react';
import { t } from 'i18next';
import { useCarePlanDownloadModal } from '@views/pages/Patients/hooks/useCarePlanDownloadModal';
import { Select } from '@ui';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  Typography,
} from '@mui/material';

interface CarePlanDownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
  ownPatients: boolean;
  teamPatients: boolean;
}

export const CarePlanDownloadModal: FC<CarePlanDownloadModalProps> = ({
  isOpen,
  onClose,
  ownPatients,
  teamPatients,
}) => {
  const {
    filters,
    programNameOptions,
    programStatusOptions,
    partnersOptions,
    handleSelectProgramStatus,
    handleSelectProgramName,
    handleSelectPartner,
    handleChangeAssignmentType,
    handleChangeFormatType,
    handleChangeCareNotesRange,
    handleGenerateReportCSV,
    isPending,
    onCloseAction,
    failureReason,
  } = useCarePlanDownloadModal({ onClose, ownPatients, teamPatients });

  return (
    <CustomModal
      isOpen={isOpen}
      title={t('patients.carePlanDownloadModal.title')}
      onClose={onCloseAction}
    >
      {failureReason && (
        <Box sx={{ mb: 2 }}>
          <StatusBanner text={failureReason} variant='lightError' />
        </Box>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Select
          sx={{
            minWidth: 220,
            display: 'flex',
            justifyContent: 'center',
          }}
          name='partner_id'
          size='medium'
          label={t('patients.carePlanDownloadModal.filters.partner')}
          value={filters.partner_id}
          onChange={handleSelectPartner}
          options={partnersOptions}
          clearable
          clearLabel='All'
        />

        <Select
          sx={{
            display: 'flex',
            justifyContent: 'center',
            bgcolor: 'white',
          }}
          name='program_name'
          size='medium'
          label={t('patients.carePlanDownloadModal.filters.program_name')}
          value={filters.program_name}
          onChange={handleSelectProgramName}
          options={programNameOptions}
          clearable
          clearLabel='All'
        />

        <Select
          sx={{
            display: 'flex',
            justifyContent: 'center',
            bgcolor: 'white',
          }}
          name='program_status'
          size='medium'
          label={t('patients.carePlanDownloadModal.filters.program_status')}
          value={filters.program_status}
          onChange={handleSelectProgramStatus}
          options={[
            {
              value: 'all_aligned_statuses',
              label: t(
                'patients.carePlanDownloadModal.filters.allAlignedStatuses',
              ),
            },
            ...programStatusOptions,
          ]}
          clearable
          clearLabel='All'
        />

        <FormControl>
          <RadioGroup
            value={filters.assignment_type}
            name='radio-assignment_type-group'
            onChange={(event, value) =>
              handleChangeAssignmentType(value as 'me' | 'my_team' | 'all')
            }
          >
            <FormControlLabel
              value='me'
              control={<Radio />}
              disabled={!ownPatients}
              label={t('patients.carePlanDownloadModal.filters.me')}
            />
            <FormControlLabel
              value='my_team'
              control={<Radio />}
              disabled={!teamPatients}
              label={t('patients.carePlanDownloadModal.filters.my_team')}
            />
            <FormControlLabel
              value='all'
              control={<Radio />}
              label={t('patients.carePlanDownloadModal.filters.all')}
            />
          </RadioGroup>
        </FormControl>

        <FormControl>
          <FormLabel>
            <Typography variant='subtitle2' sx={{ color: 'text.primary' }}>
              {t('patients.carePlanDownloadModal.filters.care_notes_range')}
            </Typography>
          </FormLabel>
          <RadioGroup
            row
            value={filters.care_notes_range}
            name='care_notes_range'
            onChange={(event, value) =>
              handleChangeCareNotesRange(value as '90_days' | 'full')
            }
          >
            <FormControlLabel
              value='90_days'
              control={<Radio />}
              label={t('patients.carePlanDownloadModal.filters.90_days')}
            />
            <FormControlLabel
              value='full'
              control={<Radio />}
              label={t('patients.carePlanDownloadModal.filters.full')}
            />
          </RadioGroup>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            {t('patients.carePlanDownloadModal.filters.rangeHelperText')}
          </Typography>
        </FormControl>

        <FormControl>
          <FormLabel>
            <Typography variant='subtitle2' sx={{ color: 'text.primary' }}>
              {t('patients.carePlanDownloadModal.filters.download_format')}
            </Typography>
          </FormLabel>
          <RadioGroup
            row
            value={filters.download_format}
            name='download_format'
            onChange={(event, value) =>
              handleChangeFormatType(value as 'zip' | 'pdf')
            }
          >
            <FormControlLabel
              value='zip'
              control={<Radio />}
              label={t('patients.carePlanDownloadModal.filters.zip')}
            />
            <FormControlLabel
              value='pdf'
              control={<Radio />}
              label={t('patients.carePlanDownloadModal.filters.pdf')}
            />
          </RadioGroup>
        </FormControl>

        <Box
          sx={{
            marginY: 3,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
          }}
        >
          <Button variant='text' onClick={onCloseAction}>
            {t('general.cancel')}
          </Button>
          <Button
            variant='contained'
            color='secondary'
            onClick={handleGenerateReportCSV}
            disabled={isPending || !!failureReason}
          >
            {t('general.download')}
          </Button>
        </Box>
      </Box>
    </CustomModal>
  );
};
